/**
 * CustomFields
 * ============
 * The renderer for company-defined custom fields, everywhere they are
 * collected: admin and platform forms, Transaction Detail, Request New Card,
 * Submit Reimbursement. One look (`Accordion`, `TextField`, `Select`,
 * `FormField`) and one set of field editors.
 *
 * The component is fully controlled: the caller owns the value record and
 * decides how it persists. That's what lets it serve two very different
 * models with the same UI:
 *   - auto-save per field (Transaction drawer): `onChange(id, next, commit)`
 *     with `commit` true triggers an immediate mutation; `onBlur` flushes a
 *     dirty text field.
 *   - submit-with-form (every formik caller, through `FormikCustomFields`):
 *     `onChange` writes into `values.customFields`, `errors` / `touched`
 *     drive validation.
 *
 * Two layouts via `variant`:
 *   - "accordion" (default): one `Accordion` per field with a
 *     requirement-status glyph, opened while unsatisfied.
 *   - "flat": labelled fields stacked with no accordion, for compact forms.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import RadioGroup from "@mui/material/RadioGroup";
import type { SxProps, Theme } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Add01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import {
  Accordion,
  AccordionGroup,
  Checkbox,
  FormField,
  Radio,
  Select,
  TextField,
} from "@outpave/ds";
import AllocationDrawer from "components/admin/allocations/AllocationDrawer";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";
import RequirementSectionTitle from "components/common/RequirementSectionTitle";
import RemovableField from "components/common/RemovableField";
import CustomFieldParentType from "enums/CustomFieldParentType";
import CustomFieldStatus from "enums/CustomFieldStatus";
import CustomFieldType from "enums/CustomFieldType";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import useCustomFieldAllocation from "hooks/useCustomFieldAllocation";
import { useAllocationTypes } from "hooks/useAllocationTypes";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import {
  useAllocationSelectOptions,
  type AllocationTypeFilter,
} from "hooks/useAllocationSelectOptions";
import {
  type CustomFieldsRecord,
  type FormikCustomField,
} from "hooks/customFields.types";
import {
  isCheckboxChecked,
  isCustomFieldVisible,
  toCheckboxValue,
} from "hooks/customFields.utils";

/** Per-field error / touched as formik stores them (single or per-entry). */
type FieldFlag<T> = T | T[] | undefined;

interface CustomFieldsProps {
  /** Field definitions to render. When omitted, fetched from `parentType`. */
  fields?: FormikCustomField[];
  /** Fetch the active fields for this parent when `fields` isn't supplied. */
  parentType?: CustomFieldParentType;
  /** Current values keyed by field id. */
  values: CustomFieldsRecord;
  /**
   * A field's values changed. `commit` is true for choice fields (which
   * should persist immediately in auto-save contexts) and false for
   * in-progress text edits (which persist on blur). Form-submit callers can
   * ignore `commit`.
   */
  onChange: (fieldId: string, next: string[], commit: boolean) => void;
  /** A field blurred: flush a dirty value (auto-save) or mark it touched. */
  onBlur?: (fieldId: string) => void;
  /** Validation messages keyed by field id (form-submit contexts). */
  errors?: Record<string, FieldFlag<string>>;
  touched?: Record<string, FieldFlag<boolean>>;
  /** Disable every input (e.g. while a mutation is in flight). */
  disabled?: boolean;
  /** "accordion" gives each field its own section; "flat" stacks them. */
  variant?: "accordion" | "flat";
  /**
   * Columns the flat variant lays its fields out in, so a form that is already
   * two-up does not drop to one column at its custom fields.
   */
  columns?: number;
  /** Layout escape for the flat variant, which callers embed in their own forms. */
  sx?: SxProps<Theme>;
}

const isFilled = (value: string | undefined): boolean =>
  value != null && String(value).trim() !== "";

/**
 * Mirrors what `generateCustomFieldsValidation` requires of a required
 * non-checkbox field: at least one entry, and EVERY entry non-empty. A `some`
 * test here is what let a multi-entry field draw a success glyph on
 * `["filled", ""]` while the empty second entry still blocked submit.
 */
const allEntriesFilled = (values: string[] | undefined): boolean => {
  const entries = values ?? [];
  return entries.length > 0 && entries.every(isFilled);
};

/**
 * One allocation picker. Its own component because the search hook holds the
 * query, the loaded pages and the seed — a form can render several allocation
 * fields, and sharing one hook would make them filter each other.
 */
const AllocationPicker: React.FC<{
  label: React.ReactNode;
  /** Names the field when the accordion, not the control, shows the label. */
  ariaLabel: string;
  allocationType: AllocationTypeFilter;
  savedId: string | null;
  savedCode?: string;
  /** The saved id's code lookup has not settled yet. */
  resolving?: boolean;
  onSelect: (id: string | null) => void;
  errorText?: string;
  required?: boolean;
  disabled?: boolean;
}> = ({
  label,
  ariaLabel,
  allocationType,
  savedId,
  savedCode,
  resolving = false,
  onSelect,
  errorText,
  required,
  disabled,
}) => {
  const { t } = useTranslation();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const allocationDrawer = useDrawerRecord<true>();
  const allocation = useAllocationSelectOptions(
    // Unseeded until the lookup settles, so the raw id is never the label.
    savedId && !resolving
      ? { id: savedId, allocationCode: savedCode ?? savedId }
      : null,
    allocationType,
  );
  // The lookup's code is fresh; a search page can come from a stale cache.
  const options = useMemo(
    () =>
      savedId && savedCode
        ? allocation.options.map((option) =>
            option.id === savedId ? { ...option, label: savedCode } : option,
          )
        : allocation.options,
    [allocation.options, savedId, savedCode],
  );
  // The Select shows the old label until an option matches, so the value waits.
  const labelPending =
    !!savedId && resolving && !options.some((option) => option.id === savedId);

  return (
    <>
      <Select
        label={label}
        aria-label={label ? undefined : ariaLabel}
        searchable
        options={options}
        loading={allocation.loading || labelPending}
        highlight={allocation.query}
        onInputChange={allocation.onInputChange}
        onScrollEnd={allocation.onScrollEnd}
        noOptionsText={allocation.noOptionsText}
        bottomAction={allocation.bottomAction}
        value={labelPending ? null : savedId}
        // An admin who finds the allocation missing can add it without
        // leaving the form. AllocationSearchAutoComplete offers the same row,
        // and dropping it here took it off every allocation custom field.
        topAction={
          isAdminOrHigher
            ? {
                label: t("Create New Allocation"),
                onClick: () => allocationDrawer.show(true),
              }
            : undefined
        }
        onChange={(id) => {
          onSelect(id);
          // The DS clear fires onChange only, so the list would stay filtered.
          if (!id) allocation.reset();
        }}
        placeholder={t("Search allocations")}
        errorText={errorText}
        required={required}
        disabled={disabled}
        fullWidth
      />
      {/* Mounted only once asked for: it runs its own queries, and every
          allocation field on the form would otherwise pay for them. */}
      {allocationDrawer.record && (
        <AllocationDrawer
          open={allocationDrawer.open}
          handleCloseDrawer={allocationDrawer.close}
          onAfterClose={allocationDrawer.afterClose}
          afterAllocationSubmit={() => allocation.reset()}
        />
      )}
    </>
  );
};

/**
 * Resolves the field's allocation type before the picker mounts. An ALLOCATION
 * field collects one type, named by `options[0]`; mounting the picker first
 * would run an unscoped search and offer allocations the field never meant to
 * accept.
 */
const AllocationField: React.FC<
  Omit<React.ComponentProps<typeof AllocationPicker>, "allocationType"> & {
    allocationTypeKey: string;
  }
> = ({ allocationTypeKey, ...rest }) => {
  const { allocationTypes, loading } = useAllocationTypes({ showAll: true });
  const allocationType = allocationTypes.find(
    (type) => type.key === allocationTypeKey,
  );

  if (loading || !allocationType) return null;

  return (
    <AllocationPicker
      allocationType={{ key: allocationType.key, name: allocationType.name }}
      {...rest}
    />
  );
};

const CustomFields: React.FC<CustomFieldsProps> = ({
  fields,
  parentType,
  values,
  onChange,
  onBlur,
  errors,
  touched,
  disabled = false,
  variant = "accordion",
  columns = 1,
  sx,
}) => {
  const { t } = useTranslation();

  // Only fetch when the caller hasn't already resolved the fields itself.
  const { customFieldSettings } = useCustomFieldRenderer({
    parentType: fields ? undefined : parentType,
  });
  const settings = useMemo(
    () => fields ?? customFieldSettings ?? [],
    [fields, customFieldSettings],
  );

  // Resolves saved allocation ids to their codes so an ALLOCATION field can
  // render its current value before any search has run.
  const { allocationMap, isResolving } = useCustomFieldAllocation(
    settings,
    values,
  );

  const visibleFields = useMemo(
    () =>
      settings.filter(
        (field): field is FormikCustomField & { id: string } =>
          !!field.id && isCustomFieldVisible(field, values[field.id] ?? [""]),
      ),
    [settings, values],
  );

  // Optional fields always read as satisfied. For a required one this has to
  // agree with the schema exactly, or the glyph says done while submit stays
  // blocked: a checkbox must be "true", and every other type needs all of its
  // entries filled, not just one.
  const isSatisfied = (field: FormikCustomField & { id: string }) => {
    if (!field.isRequired) return true;
    const fieldValues = values[field.id];
    return field.type === CustomFieldType.CHECKBOX
      ? isCheckboxChecked(fieldValues ?? [])
      : allEntriesFilled(fieldValues);
  };

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const unsatisfied = visibleFields
    .filter((field) => !isSatisfied(field))
    .map((field) => field.id);
  // Keyed on the ids rather than on `values`, so this runs on the transition
  // instead of on every keystroke.
  const unsatisfiedKey = unsatisfied.join(",");

  useEffect(() => {
    const ids = unsatisfiedKey ? unsatisfiedKey.split(",") : [];
    if (ids.length === 0) return;
    // Force a section open by ADDING it to the open set. Overriding the
    // `expanded` prop instead makes a collapse click read as inert, and drops
    // the section shut under the user the moment it becomes satisfied.
    setExpanded((prev) => {
      const missing = ids.filter((id) => !prev[id]);
      if (missing.length === 0) return prev;
      return {
        ...prev,
        ...Object.fromEntries(missing.map((id) => [id, true])),
      };
    });
  }, [unsatisfiedKey]);

  if (visibleFields.length === 0) return null;

  // Touched-and-errored message for one entry of a field, else undefined.
  const entryError = (fieldId: string, index: number): string | undefined => {
    if (!errors || !touched) return undefined;
    const rawError = errors[fieldId];
    const rawTouched = touched[fieldId];
    const errorArray = Array.isArray(rawError)
      ? rawError
      : rawError != null
        ? [rawError]
        : [];
    const touchedArray = Array.isArray(rawTouched)
      ? rawTouched
      : rawTouched != null
        ? [rawTouched]
        : [];
    const message = errorArray[index];
    const isTouched = touchedArray[index] ?? touchedArray[0] ?? false;
    return isTouched && typeof message === "string" ? message : undefined;
  };

  // A field label is authored by an admin, so a bare t() would read a period
  // as an object path and a colon as a namespace and return the wrong half.
  const fieldLabel = (field: FormikCustomField) =>
    t(field.label, { nsSeparator: false, keySeparator: false });

  const renderEditor = (
    field: FormikCustomField & { id: string },
    showLabel: boolean,
  ) => {
    const fieldValues = values[field.id] ?? [""];
    const readOnly = field.status === CustomFieldStatus.READONLY;
    const isDisabled = disabled || readOnly;
    const label = showLabel ? fieldLabel(field) : undefined;

    switch (field.type) {
      case CustomFieldType.CHECKBOX:
        // No `label` on the FormField: the FormControlLabel already carries it,
        // and its own `required` draws the marker. This is here for `errorText`,
        // which a required checkbox needs to explain what is blocking submit.
        return (
          <FormField
            errorText={entryError(field.id, 0)}
            disabled={isDisabled}
            fullWidth
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCheckboxChecked(fieldValues)}
                  onChange={(event) =>
                    onChange(
                      field.id,
                      [toCheckboxValue(event.target.checked)],
                      true,
                    )
                  }
                  disabled={isDisabled}
                />
              }
              label={fieldLabel(field)}
              required={field.isRequired}
            />
          </FormField>
        );

      case CustomFieldType.LIST:
        return (
          <Select
            label={label}
            aria-label={label ? undefined : fieldLabel(field)}
            options={(field.options ?? []).map((option) => ({
              id: option,
              label: option,
            }))}
            value={fieldValues[0] || null}
            // The DS clear hands back null, and swallowing it is what used to
            // make an optional list impossible to empty once set.
            onChange={(id) => onChange(field.id, [id ?? ""], true)}
            placeholder={t("Select an option")}
            errorText={entryError(field.id, 0)}
            required={field.isRequired}
            disabled={isDisabled}
            fullWidth
          />
        );

      case CustomFieldType.RADIO:
        return (
          <FormField
            asFieldset
            label={label}
            required={field.isRequired}
            errorText={entryError(field.id, 0)}
            disabled={isDisabled}
            fullWidth
          >
            <RadioGroup
              aria-label={label ? undefined : fieldLabel(field)}
              value={fieldValues[0] || ""}
              onChange={(event) =>
                onChange(field.id, [event.target.value], true)
              }
            >
              {!field.isRequired && (
                <FormControlLabel
                  value=""
                  control={<Radio disabled={isDisabled} />}
                  label={t("None")}
                />
              )}
              {(field.options ?? []).map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio disabled={isDisabled} />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormField>
        );

      case CustomFieldType.ALLOCATION: {
        const savedId = fieldValues[0] || null;
        return (
          <AllocationField
            allocationTypeKey={field.options?.[0] ?? ""}
            label={label}
            ariaLabel={fieldLabel(field)}
            savedId={savedId}
            savedCode={savedId ? allocationMap[savedId] : undefined}
            resolving={!!savedId && isResolving(savedId)}
            onSelect={(id) => onChange(field.id, [id ?? ""], true)}
            errorText={entryError(field.id, 0)}
            required={field.isRequired}
            disabled={isDisabled}
          />
        );
      }

      case CustomFieldType.TEXT:
      default:
        return (
          <Stack gap="var(--space-3)">
            {fieldValues.map((value, index) => (
              <RemovableField
                // Entries have no stable identity; the list only changes via
                // add / remove, which re-renders everything anyway.
                key={index}
                removeLabel={t("Delete")}
                disabled={isDisabled}
                onRemove={
                  field.allowMultipleEntries &&
                  fieldValues.length > 1 &&
                  !readOnly
                    ? () =>
                        onChange(
                          field.id,
                          fieldValues.filter((_, i) => i !== index),
                          true,
                        )
                    : undefined
                }
              >
                <TextField
                  label={index === 0 ? label : undefined}
                  aria-label={label ? undefined : fieldLabel(field)}
                  value={value}
                  onChange={(event) =>
                    onChange(
                      field.id,
                      fieldValues.map((entry, i) =>
                        i === index ? event.target.value : entry,
                      ),
                      false,
                    )
                  }
                  onBlur={() => onBlur?.(field.id)}
                  errorText={entryError(field.id, index)}
                  required={field.isRequired}
                  disabled={isDisabled}
                  fullWidth
                />
              </RemovableField>
            ))}
            {field.allowMultipleEntries && !readOnly && (
              <Inline>
                <Button
                  variant="primary-soft"
                  size="small"
                  startIcon={<HugeiconsIcon icon={Add01Icon} size={16} />}
                  onClick={() =>
                    onChange(field.id, [...fieldValues, ""], false)
                  }
                  disabled={disabled}
                >
                  {t("Add Additional Option")}
                </Button>
              </Inline>
            )}
          </Stack>
        );
    }
  };

  if (variant === "flat") {
    return (
      <Columns count={columns} gap="var(--space-5)" sx={sx}>
        {visibleFields.map((field) => (
          <div key={field.id}>{renderEditor(field, true)}</div>
        ))}
      </Columns>
    );
  }

  return (
    <AccordionGroup>
      {visibleFields.map((field) => (
        <Accordion
          key={field.id}
          title={
            <RequirementSectionTitle ok={isSatisfied(field)}>
              {fieldLabel(field)}
            </RequirementSectionTitle>
          }
          expanded={!!expanded[field.id]}
          onChange={(_event, isExpanded) =>
            setExpanded((prev) => ({ ...prev, [field.id]: isExpanded }))
          }
        >
          {renderEditor(field, false)}
        </Accordion>
      ))}
    </AccordionGroup>
  );
};

export default CustomFields;
